import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { cardPriceFootnotes, isMonthlyCardPrice } from 'app/shared/models/dzbank-configuration.model';
import { ExpandableComponent } from 'app/shared/components/expandable/expandable.component';
import { ButtonComponent } from 'app/shared/components/button/button.component';
import { MainLayoutComponent } from 'app/shared/components/layouts/main-layout/main-layout.component';
import { Themes } from 'themes';
import { ExpandableCheckboxComponent } from 'app/shared/components/expandable-checkbox/expandable-checkbox.component';
import {
  bankInfoText,
  ExpandableText,
  feeInfoText,
  moneyLaunderingText,
  schufaText,
  sepaText,
} from 'app/shared/models/expandable-texts';
import { BankAccountInfoComponent } from 'app/shared/components/bank-account-info/bank-account-info.component';
import { EmailInputComponent } from 'app/shared/components/email-input/email-input.component';
import { emailValidator } from 'app/shared/components/email-input/email-input.validator';
import { BackendService } from 'app/shared/services/backend.service';
import { MandatoryAcceptances } from 'app/shared/models/dto/email-request-dto';
import { StepperComponent } from 'app/shared/components/stepper/stepper.component';
import { CreditCardImageComponent } from 'app/shared/components/credit-card-image/credit-card-image.component';
import { lastValueFrom } from 'rxjs';
import { BackendErrorType } from 'app/shared/services/errors/backend.errors';
import { LandingpageConfigurationStateService } from 'app/shared/services/state/landingpage-configuration-state.service';
import { InfoButtonComponent } from 'app/shared/components/info-button';
import { PopoverComponent } from 'app/shared/components/popover';
import { CardPriceFootnotesComponent } from 'app/shared/components/card-price-footnotes/card-price-footnotes.component';
import { CurrencyPipe } from '@angular/common';
import { RequiredFieldsHintComponent } from 'app/shared/components/required-fields-hint/required-fields-hint.component';

@Component({
  selector: 'ez-overview',
  templateUrl: './overview.component.html',
  styleUrl: './overview.component.scss',
  imports: [
    ExpandableComponent,
    ReactiveFormsModule,
    ButtonComponent,
    MainLayoutComponent,
    ExpandableCheckboxComponent,
    BankAccountInfoComponent,
    EmailInputComponent,
    StepperComponent,
    CreditCardImageComponent,
    InfoButtonComponent,
    PopoverComponent,
    CardPriceFootnotesComponent,
    CurrencyPipe,
    RequiredFieldsHintComponent,
  ],
  providers: [],
})
export class OverviewComponent {
  protected readonly SEPA_ACCEPTED_CONTROL_NAME = 'isSepaMandateAccepted';
  protected readonly MONEY_LAUNDERING_ACCEPTED_CONTROL_NAME = 'isMoneyLaunderingAccepted';
  protected readonly BANKINFO_ACCEPTED_CONTROL_NAME = 'isBankInfoAccepted';
  protected readonly EMAIL_CONTROL_NAME = 'email';

  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly backendService = inject(BackendService);
  private readonly landingpageConfigurationStateService = inject(LandingpageConfigurationStateService);

  readonly submitEmailAndAcceptedFieldsRequestIsLoading = signal(false);
  readonly customData = this.landingpageConfigurationStateService.customData;
  readonly customer = this.landingpageConfigurationStateService.customer;
  readonly assetStackBucketUrl = this.landingpageConfigurationStateService.assetStackBucketUrl;
  readonly cardInformation = this.landingpageConfigurationStateService.cardInformation;
  readonly cardProductTexts = this.landingpageConfigurationStateService.cardProductTexts;
  readonly ibanAnonymised = this.landingpageConfigurationStateService.ibanAnonymised;
  readonly organisation = this.landingpageConfigurationStateService.organisation;

  readonly smallLogoUrl = computed<string | undefined>(() => {
    const smallLogo = this.organisation()?.corporateDesign.logos.smallLogo;
    return this.assetStackBucketUrl() && smallLogo ? this.assetStackBucketUrl() + smallLogo : undefined;
  });

  readonly form = this.formBuilder.nonNullable.group({
    [this.BANKINFO_ACCEPTED_CONTROL_NAME]: [false, Validators.requiredTrue],
    [this.MONEY_LAUNDERING_ACCEPTED_CONTROL_NAME]: [false, Validators.requiredTrue],
    [this.SEPA_ACCEPTED_CONTROL_NAME]: [false, Validators.requiredTrue],
    [this.EMAIL_CONTROL_NAME]: ['', [Validators.required, emailValidator]],
  });

  protected readonly sepaText = computed<ExpandableText | undefined>(() => {
    const organisation = this.organisation();
    return organisation != null ? sepaText(organisation) : undefined;
  });

  readonly infoButton = viewChild.required(InfoButtonComponent);
  readonly showPopover = signal(false);

  get infoButtonEl(): HTMLElement {
    return this.infoButton().icon().nativeElement;
  }

  onSubmit() {
    const acceptances: MandatoryAcceptances = {
      isBankInfoAccepted: this.form.controls[this.BANKINFO_ACCEPTED_CONTROL_NAME].value,
      isMoneyLaunderingAccepted: this.form.controls[this.MONEY_LAUNDERING_ACCEPTED_CONTROL_NAME].value,
      isSepaMandateAccepted: this.form.controls[this.SEPA_ACCEPTED_CONTROL_NAME].value,
    };

    this.submitEmailAndAcceptedFieldsRequestIsLoading.set(true);
    const email = this.form.controls[this.EMAIL_CONTROL_NAME].value;
    lastValueFrom(this.backendService.submitEmailAndAcceptedFields(email, acceptances))
      .then(() => {
        this.landingpageConfigurationStateService.setCustomerEmail(email);
        this.router.navigate(['verify-email']);
      })
      .catch((error) => {
        if (error.message === BackendErrorType.ALREADY_SENT) {
          this.router.navigate(['already-sent']);
        } else if (error.message === BackendErrorType.VERIFICATION_ERROR) {
          this.router.navigate(['verification-error']);
        }
      })
      .finally(() => {
        this.submitEmailAndAcceptedFieldsRequestIsLoading.set(false);
      });
  }

  protected readonly isMonthlyCardPrice = isMonthlyCardPrice;
  protected readonly cardPriceFootnotes = cardPriceFootnotes;
  protected readonly Themes = Themes;
  protected readonly feeInfoText = feeInfoText;
  protected readonly bankInfoText = bankInfoText;
  protected readonly schufaText = schufaText;
  protected readonly moneyLaunderingText = moneyLaunderingText;
}
